import React, {useEffect, useState} from "react";
import {Controller, useFormContext} from "react-hook-form";
import BaseWrapper from "../BaseWrapper";
import {ClayInput} from "@clayui/form";
import DropDown from "@clayui/drop-down";
import ClayIcon from "@clayui/icon";
import useDebounce from "../../../hooks/useDebounce";
import classNames from "classnames";
import {Street} from "../../../dto/dto";
import LoadingIndicator from "@clayui/loading-indicator";

type OptionType = {
    name?: string;
    id?: number | string;
};

type FormDropdownAutocompleteProps = {
    name: string;
    label?: string;
    options: OptionType[];
    additionalOnChange?: (value: number | string | null) => void;
    groupClassName?: string;
    required?: boolean;
    disabled?: boolean;
    searchPlaceholder?: string;
    placeholder?: string;
    cityId?: string;
    autocompleteStreets: (cityId: string, query: string) => Promise<Street[]>;
    onChangeWithFocus?: (elementId: string) => void;
    isLoading?: boolean;
};

export function FormDropdownAutocomplete(props: FormDropdownAutocompleteProps) {
    const { cityId, autocompleteStreets } = props
    const { formState } = useFormContext();
    const [searchValue, setSearchValue] = useState("");
    const [filteredOptions, setFilteredOptions] = useState<OptionType[]>([]);

    const debouncedSearchValue = useDebounce(searchValue, 100);

    useEffect(() => {
        const fetchOptions = async () => {
            if (!cityId || debouncedSearchValue.length < 2) {
                setFilteredOptions(props.options);
                return;
            }
            const result = await autocompleteStreets(cityId, debouncedSearchValue);
            setFilteredOptions(result);
        };

        fetchOptions();
    }, [cityId, debouncedSearchValue, autocompleteStreets, props.options]);




    return (
        <Controller
            name={props.name}
            render={({field, fieldState}) => (
                <BaseWrapper
                    required={props.required}
                    error={fieldState?.error?.message}
                    id={props.name}
                    label={props.label}
                    groupClassName={props.groupClassName}
                    disabled={props.disabled || formState.isLoading}
                >
                    <DropDown
                        onActiveChange={(active: boolean) => {
                            if (!active) {
                                setSearchValue('');
                            }
                        }}
                        menuWidth='sm'
                        filterKey="label"
                        closeOnClick
                        trigger={
                            <div className="input-group" role="button" aria-label={props.label}>
                                <div className="input-group-item">
                                    <ClayInput
                                        id={props.name}
                                        className="form-control input-group-inset input-group-inset-after text-ellipsis"
                                        style={{cursor: "pointer"}}
                                        value={!field.value
                                            ? searchValue
                                            : filteredOptions.find(opt => opt.id === field.value)?.name
                                        }
                                        disabled={props.disabled}
                                        placeholder={props.placeholder}
                                        required={props.required}
                                    />
                                    <div className="input-group-inset-item input-group-inset-item-after">
                                        {field.value && (
                                            <button className='btn btn-unstyled'
                                                    disabled={props.disabled || formState.isLoading}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault()
                                                        field.onChange(null);
                                                        props.additionalOnChange?.(null);
                                                    }}
                                            >
                                                <ClayIcon
                                                    focusable={false}
                                                    role="presentation"
                                                    symbol="times"/>
                                            </button>
                                        )}
                                        <div
                                            className={classNames("btn btn-unstyled", { disabled: props.disabled || formState.isLoading })}>
                                            <ClayIcon
                                                focusable={false}
                                                role="presentation"
                                                symbol="caret-double"/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }>
                        <DropDown.Search
                            placeholder={props.searchPlaceholder}
                            value={searchValue}
                            onChange={setSearchValue}
                        />
                        <DropDown.ItemList items={filteredOptions}>
                            {(item: OptionType) => (
                                <DropDown.Item
                                    disabled={props.disabled}
                                    key={item.id}
                                    onClick={() => {
                                        props.onChangeWithFocus?.(props.name);
                                        field.onChange(item.id);
                                        props.additionalOnChange?.(item.id ?? null);
                                    }}
                                >
                                    {item.name}
                                </DropDown.Item>
                            )}
                        </DropDown.ItemList>
                    </DropDown>
                    {props.isLoading && (
                        <div className="mr-2" style={{
                            position: 'absolute',
                            right: '50px',
                            top: '38px',
                        }}>
                            <LoadingIndicator style={{margin: 0}} displayType="secondary" size="sm"/>
                        </div>
                    )}
                </BaseWrapper>
            )}
        />
    );
}

export default FormDropdownAutocomplete;
